import { Center, Flex, Span, Text } from '@chakra-ui/react';
import {
  EmptyState,
  OverlayDialog,
  toasterApi,
  ViewAttachmentsCard,
} from '@ui';
import React, { Suspense } from 'react';

const ViewAttachments = React.lazy(() => import('@ui/view-attachment'));

type ReturnAttachment = {
  id: number;
  rfi?: number | null;
  submittal?: number | null;
  filename?: string | null;
  file_url?: string | null;
  created_at: Date | string;
};

type ViewReturnAttachmentsCardProps = {
  attachments: ReturnAttachment[];
  onDelete: (returnId: number) => Promise<void>;
};

const ViewReturnAttachmentsCard = ({
  attachments,
  onDelete,
}: ViewReturnAttachmentsCardProps) => {
  if (!attachments?.length) {
    return (
      <Center width="full">
        <EmptyState
          isEmpty
          position="relative"
          title="No returned documents available"
          description="Please upload returned documents to view them here"
        />
      </Center>
    );
  }

  return (
    <Suspense fallback={<Text>Loading attachments...</Text>}>
      <ViewAttachments
        attachments={
          attachments?.map((item) => ({
            id: item?.id,
            filename: item?.filename ?? '',
            file: item?.file_url ?? '',
          })) ?? []
        }
        onSelect={({ file, filename }) => {
          OverlayDialog.open('viewReturnAttachment', {
            overlayId: 'viewReturnAttachment',
            title: 'View Returned Document',
            description: (
              <Flex>
                <Span textStyle="heading.title" color="secondary.fg">
                  File name:&nbsp;
                </Span>
                <Span>{filename}</Span>
              </Flex>
            ),
            content: <ViewAttachmentsCard filename={filename} file={file} />,
          });
        }}
        onDelete={({ id, file, filename }) => {
          OverlayDialog.open('deleteReturnAttachment', {
            overlayId: 'deleteReturnAttachment',
            title: 'Delete Returned Document',
            description: (
              <Flex flexDirection="column" gapY={2}>
                <Text>
                  Are you sure you want to delete this returned document? This
                  action is irreversible.
                </Text>
                <Flex>
                  <Span textStyle="heading.title" color="secondary.fg">
                    File name:&nbsp;
                  </Span>
                  <Span>{filename}</Span>
                </Flex>
              </Flex>
            ),
            content: <ViewAttachmentsCard filename={filename} file={file} />,
            handleConfirm: async () => {
              try {
                await onDelete(id);
                toasterApi({
                  type: 'success',
                  status: 204,
                  code: 'Success',
                  message: 'Returned document deleted successfully',
                });
              } catch {
                toasterApi({
                  type: 'error',
                  status: 500,
                  code: 'Error',
                  message: 'Could not delete returned document',
                });
              } finally {
                OverlayDialog.close('deleteReturnAttachment');
              }
            },
          });
        }}
      />
    </Suspense>
  );
};

export default ViewReturnAttachmentsCard;
